import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { BorderlessDropdown, SpacerVertical, SwitchToggle } from '@klarna/bubble-ui';
import ComboBox from '@bubble-contrib/molecule-combobox';
import { FieldsetForObject, formUtils, MOET_CONSTANTS } from 'moet-core';

import { validators } from '@/flow/validation';

const { WIDGET_TYPE } = MOET_CONSTANTS;

export const FlowOptionsMenu = ({
  onChange,
  countries = [],
  value: { country, region, showExcludedSteps: showExcludedStepsProp = false, widgetType } = {},
}) => {
  const countryObject = { item: countries.find(({ key }) => key === country) };

  const [showExcludedSteps, setShowExcludedSteps] = useState(showExcludedStepsProp);

  const [fields, setFields] = useState(
    formUtils.initForm({
      region: {
        component: BorderlessDropdown,
        size: 1 / 3,
        label: 'Region',
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: 'Region is required',
          },
        ],
        extraProps: {
          options: ['EU', 'US', 'AP'].map(e => ({ label: e, key: e.toLowerCase() })),
          onBlur: () => {},
        },
        isInitialized: false,
        isValid: false,
        value: region,
      },
      country: {
        component: ComboBox,
        size: 1 / 3,
        label: 'Country',
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: 'Country is required',
          },
        ],
        extraProps: {
          options: countries,
          borderless: true,
          showOptions: true,
          showSuggestions: true,
          onBlur: () => {},
        },
        isInitialized: false,
        isValid: false,
        value: countryObject,
      },
      widgetType: {
        component: BorderlessDropdown,
        size: 1 / 3,
        label: 'Widget type',
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: 'Widget type is required',
          },
        ],
        extraProps: {
          options: Object.keys(WIDGET_TYPE)
            .filter(t => t !== WIDGET_TYPE.RECOLLECTION_KYC)
            .map(k => ({ label: k, key: k })),
          onBlur: () => {},
        },
        isInitialized: false,
        isValid: false,
        value: widgetType,
      },
    }),
  );

  const onChangeOption = (key, value) => {
    formUtils.onChangeFields(key, value, { fields }, fields => {
      setFields(fields);
      onChange({
        country: fields.country.value?.item?.key,
        region: fields.region.value,
        showExcludedSteps,
        widgetType: fields.widgetType.value,
      });
    });
  };

  const onChangeShowExcluded = showExcludedSteps => {
    setShowExcludedSteps(showExcludedSteps);
    onChange({
      country: fields.country.value?.item?.key,
      region: fields.region.value,
      showExcludedSteps,
      widgetType: fields.widgetType.value,
    });
  };

  return (
    <>
      <FieldsetForObject
        isBorderless
        tabIndexStart={1}
        name="flow-render-options"
        fields={fields}
        onChange={onChangeOption}
      />
      <SpacerVertical spaceToken="space/100" />
      <SwitchToggle name="toggle" checked={showExcludedSteps} onChange={onChangeShowExcluded}>
        Show excluded steps
      </SwitchToggle>
    </>
  );
};

FlowOptionsMenu.propTypes = {
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  value: PropTypes.shape({
    country: PropTypes.string,
    region: PropTypes.string,
    showExcludedSteps: PropTypes.bool,
    widgetType: PropTypes.string,
  }),
  onChange: PropTypes.func,
};
